import { defineRoutes } from '@makan/contracts/client';

/**
 * feedback's values entry: the facts the pages, the toolkit and the checks
 * read by value, with no zod (rule R20). commands.ts builds its schemas on
 * these, so the toolkit and the decider cannot disagree.
 */

export const routes = defineRoutes({
  inbox: { path: '/feedback', access: 'staff', intent: 'The feedback staff and testers sent from any screen: each item with its picture or recording, where it came from, and its status and replies.' },
});

/** What a tester says an item is. */
export const FEEDBACK_KINDS = ['bug', 'idea', 'blocker'] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

/** Where an item stands. `sent to github` is set only when the issue opened, never by hand. */
export const FEEDBACK_STATUSES = ['new', 'seen', 'in progress', 'sent to github', 'fixed', "won't do"] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];
export const SETTABLE_STATUSES = ['new', 'seen', 'in progress', 'fixed', "won't do"] as const satisfies readonly FeedbackStatus[];

/**
 * Who sees the toolkit, a group setting (`set feedback toolkit`):
 *   environment  every staff member in local and staging; nobody in test and production (the default)
 *   testers      as `environment`, and in test and production the staff the group names as testers
 *   off          nobody, anywhere
 */
export const TOOLKIT_MODES = ['environment', 'testers', 'off'] as const;
export type ToolkitMode = (typeof TOOLKIT_MODES)[number];
export const DEFAULT_TOOLKIT_MODE: ToolkitMode = 'environment';

/** Where a cloud runs (MAKAN_ENV), as the session tells the page. */
export const ENVIRONMENTS = ['local', 'test', 'staging', 'production'] as const;
export type Environment = (typeof ENVIRONMENTS)[number];

/** Whether a signed-in staff member sees the toolkit: the one rule the page applies before it loads any of the toolkit's code. */
export function toolkitShows(input: { readonly environment: Environment; readonly mode: ToolkitMode; readonly testers: readonly string[]; readonly staffId: string }): boolean {
  if (input.mode === 'off') return false;
  if (input.environment === 'local' || input.environment === 'staging') return true;
  return input.mode === 'testers' && input.testers.includes(input.staffId);
}

/** Bounds on what one item carries. */
export const FEEDBACK_LIMITS = {
  description: 2000,
  reply: 1000,
  steps: 30,
  problems: 20,
  media: 3,
  label: 80,
  text: 300,
  path: 300,
} as const;

/** Up to this long a screen recording runs (desktop); a voice note on a phone, VOICE_SECONDS. */
export const RECORDING_SECONDS = 180;
export const VOICE_SECONDS = 120;

/**
 * The media an item carries, by slot: the types the cloud takes and the most
 * bytes one upload may hold. The cloud checks both on the bytes it streams
 * (the manifest's `media`), whatever the browser claims.
 */
export const MEDIA_SLOTS = {
  screenshot: { types: ['image/png', 'image/jpeg'], maxBytes: 5 * 1024 * 1024 },
  recording: { types: ['video/webm', 'video/mp4'], maxBytes: 100 * 1024 * 1024 },
  voice: { types: ['audio/webm', 'audio/mp4', 'audio/ogg'], maxBytes: 10 * 1024 * 1024 },
} as const;
export type MediaSlot = keyof typeof MEDIA_SLOTS;
export const MEDIA_SLOT_NAMES = Object.keys(MEDIA_SLOTS) as readonly MediaSlot[];
export const MEDIA_TYPES = ['image/png', 'image/jpeg', 'video/webm', 'video/mp4', 'audio/webm', 'audio/mp4', 'audio/ogg'] as const;
export type MediaType = (typeof MEDIA_TYPES)[number];

/** A stored medium's id as the cloud answers an upload: a UUIDv7 and the extension of its type. */
export const MEDIA_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.(png|jpg|webm|mp4|m4a|ogg)$/;

/** A step a tester took before sending, as the toolkit names it. */
export const STEP_KINDS = ['navigate', 'click', 'command'] as const;
/** A problem the page saw before sending. */
export const PROBLEM_KINDS = ['console', 'exception', 'rejection', 'request'] as const;
/** The roles a clicked element is named by; anything else is not recorded. */
export const STEP_ROLES = ['button', 'link', 'tab', 'menuitem', 'option', 'checkbox', 'radio', 'switch'] as const;

/** Query keys the toolkit keeps on a recorded path: navigation, never a code, a pass or a phone. Every other key is dropped. */
export const SAFE_QUERY_KEYS = ['tab', 'view', 'status', 'kind', 'lang', 'item'] as const;

/** A path with its query reduced to the safe keys and no fragment. */
export function cleanPath(pathAndQuery: string): string {
  const [beforeHash = ''] = pathAndQuery.split('#');
  const at = beforeHash.indexOf('?');
  const path = at === -1 ? beforeHash : beforeHash.slice(0, at);
  const query = at === -1 ? '' : beforeHash.slice(at + 1);
  const kept = query
    .split('&')
    .filter((pair) => (SAFE_QUERY_KEYS as readonly string[]).includes(decodeSafely(pair.split('=')[0] ?? '')))
    .join('&');
  const cleaned = (path.startsWith('/') ? path : `/${path}`).replace(/\s+/g, '');
  return (kept === '' ? cleaned : `${cleaned}?${kept}`).slice(0, FEEDBACK_LIMITS.path);
}

function decodeSafely(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

/**
 * Text the toolkit records (a button's name, an error message) with what
 * could be a person taken out: an email address, a run of six or more digits
 * (a phone, a code), spaces collapsed, cut to `max`. A field's value is never
 * read at all.
 */
export function scrub(text: string, max: number = FEEDBACK_LIMITS.text): string {
  return text
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[email]')
    .replace(/\+?\d[\d\s-]{4,}\d/g, (run) => (run.replace(/\D/g, '').length >= 6 ? '[number]' : run))
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}
